<template>
  <div
    class="bl-input-component"
    :class="{
      'bl-input-focus': focused,
      'bl-input-disabled': input.disabled
    }"
    :data-id="input.id"
  >
    <label
      v-if="input.label"
      :for="id"
      :data-id="input.id"
      class="bl-input-label"
    >
      {{ input.label }}
      <span
        v-if="input.asterisk"
        class="absolute ml-1 text-xl text-error-400 -top-2"
      >
        *
      </span>
    </label>
    <BlIcon
      :icon="inputType === 'time' ? 'clock' : 'calendar-blank'"
      class="bl-input-icon"
      :class="{
        'bl-input-icon-label': input.label && !dense,
        'bl-input-icon-label-dense': input.label && dense,
        'bl-input-icon-dense': !input.label && dense
      }"
      :color="focused ? 'primary' : ''"
    />
    <input
      :type="inputType"
      class="bl-input"
      :class="{
        'bg-grey-100 rounded': input.disabled,
        'bl-input-full-indicator': disabledManualInput,
        'bl-input-dense': dense
      }"
      :id="id"
      :min="input.min"
      :max="input.max"
      :placeholder="input.placeholder"
      :data-id="input.id"
      :disabled="input.disabled"
      v-model="value"
      @focusin="focused = true"
      @blur="focused = false"
      @input="validateYear"
      :required="input.asterisk"
    />
    <div class="bl-input-border" :class="{ 'bl-input-dense': dense }"></div>
  </div>
</template>

<script>
import BlIcon from "./BlIcon.vue";
export default {
  components: { BlIcon },
  props: {
    dense: { type: Boolean, default: false },
    input: {
      model: {
        type: String,
        required: true
      },
      inputType: {
        type: String,
        default: "date"
      },
      label: {
        type: String,
        required: false
      },
      disabled: {
        type: Boolean,
        required: false
      },
      id: {
        type: String,
        required: false
      },
      asterisk: {
        type: Boolean,
        default: false
      },
      min: {
        type: String,
        required: false
      },
      max: {
        type: String,
        required: false
      }
    },
    disabledManualInput: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      value: "",
      focused: false,
      lastValidValue: ""
    };
  },
  computed: {
    id() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      return `bl-input-${combination}`;
    },
    inputType() {
      return this.input.inputType === "time" ? "time" : "date";
    }
  },
  methods: {
    validateYear(event) {
      if (this.inputType !== "date") return;

      const input = event.target;
      const dateValue = input.value;

      // Check if the date format includes a year with more than 4 digits
      if (dateValue.length >= 10) {
        const parts = dateValue.split("-");
        if (parts.length === 3 && parts[0].length > 4) {
          // Revert to the last valid value if year has more than 4 digits
          this.value = this.lastValidValue;
          return;
        }
      }

      // Update last valid value
      this.lastValidValue = dateValue;
      this.value = dateValue;

      // Emit the change event
      if (!this.disabled) {
        this.$emit("changed", {
          input: this.input,
          value: dateValue
        });
      }
    }
  },
  watch: {
    value(val) {
      if (!this.disabled) {
        this.$emit("changed", {
          input: this.input,
          value: val
        });
      }
    },
    input: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val.model) {
          this.value = val.model;
          this.lastValidValue = val.model;
        } else {
          this.value = "";
          this.lastValidValue = "";
        }
      }
    }
  },
  mounted() {
    if (this.input.model) {
      this.value = this.input.model;
      this.lastValidValue = this.input.model;
    }
  }
};
</script>

<style scoped lang="scss">
/* Your existing styles remain unchanged */
.bl-input-component {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  .bl-input-label {
    position: relative;
    font-size: 0.75rem;
    font-weight: 400;
    line-height: calc(5 / 3);
    letter-spacing: 0.5px;
    margin-bottom: 0.25rem;
    padding: 0;
    & ~ .bl-input-border {
      top: 1.5rem;
    }
  }
  .bl-input {
    position: relative;
    width: 100%;
    height: 2.5rem;
    padding-right: 1rem;
    padding-left: 1rem;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: calc(11 / 7);
    letter-spacing: 0.5px;
    box-sizing: border-box;
    flex-shrink: 0;
    &:focus {
      outline: none;
    }
    &::-webkit-calendar-picker-indicator {
      position: absolute;
      opacity: 0;
      top: 0.5rem;
      right: 1rem;
      width: 1.5rem;
      height: 1.5rem;
    }
    &.bl-input-full-indicator::-webkit-calendar-picker-indicator {
      position: absolute;
      opacity: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
      bottom: 0;
      left: 0;
      right: 0;
      top: 0;
    }
  }
  .bl-input-border {
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2.5rem;
    border-radius: 5px;
    box-sizing: border-box;
    border: 1px solid var(--border-color);
  }
  .bl-input-dense {
    height: 2rem;
    padding-right: 0.75rem;
    padding-left: 0.75rem;
    &::-webkit-calendar-picker-indicator {
      top: 0.25rem;
      right: 1rem;
      width: 1.5rem;
      height: 1.5rem;
    }
  }
  .bl-input-icon {
    z-index: 2;
    pointer-events: none;
    position: absolute;
    top: 0.5rem;
    right: 1rem;
    width: 1.5rem;
    height: 1.5rem;
    &.bl-input-icon-label {
      top: 2rem;
      right: 1rem;
    }
    &.bl-input-icon-label-dense {
      top: 1.75rem;
      right: 0.5rem;
    }
    &.bl-input-icon-dense {
      top: 0.25rem;
      right: 0.5rem;
    }
  }
  &.bl-input-disabled {
    pointer-events: none;
    opacity: 0.5;
  }
  &.bl-input-focus {
    .bl-input-border {
      border-color: var(--primary-color);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/BlInputDate.vue